// Cart Service for NLFX Cable Builder
// Handles cart persistence using localStorage for reliable mobile experience

const CART_STORAGE_KEY = 'nlfx_cart';
const CART_TIMESTAMP_KEY = 'nlfx_cart_timestamp';
const CART_EXPIRY_DAYS = 30; // Cart expires after 30 days

class CartService {
  constructor() {
    this.listeners = [];
    this._wasRestoredDuringInit = false;
    this._hasFreshOperations = false; // Track if there have been fresh add/remove operations
    
    // Test localStorage availability first
    if (!this.testLocalStorage()) {
      console.warn('localStorage not available, cart persistence disabled');
      this.cart = [];
      return;
    }
    
    this.cart = this.loadCart();
    this.handleMobileIssues();
  }

  // Load cart from localStorage
  loadCart() {
    try {
      const cartData = localStorage.getItem(CART_STORAGE_KEY);
      const timestamp = localStorage.getItem(CART_TIMESTAMP_KEY);
      
      if (!cartData || !timestamp) {
        return [];
      }

      // Check if cart has expired
      const now = Date.now();
      const cartAge = now - parseInt(timestamp);
      const expiryMs = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      
      if (cartAge > expiryMs) {
        this.clearCart();
        return [];
      }

      const cart = JSON.parse(cartData);
      
      // Mark that cart was restored during initialization
      if (cart && cart.length > 0) {
        this._markRestoredDuringInit();
      }
      
      return Array.isArray(cart) ? cart : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  }

  // Check if cart was restored from localStorage
  wasRestoredFromStorage() {
    return localStorage.getItem(CART_TIMESTAMP_KEY) !== null;
  }

  // Check if cart was actually restored (not just newly created)
  wasActuallyRestored() {
    const timestamp = localStorage.getItem(CART_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    // If this is the first time loading the page, the cart was restored
    // We can detect this by checking if the cart was loaded during initialization
    return this._wasRestoredDuringInit;
  }

  // Check if there have been fresh operations (add/remove) since page load
  hasFreshOperations() {
    return this._hasFreshOperations;
  }

  // Mark that cart was restored during initialization
  _markRestoredDuringInit() {
    this._wasRestoredDuringInit = true;
  }

  // Save cart to localStorage
  saveCart(cart) {
    try {
      // Limit cart size to prevent localStorage quota exceeded errors on mobile
      const maxCartSize = 50; // Maximum number of items
      const maxItemSize = 10000; // Maximum size per item in characters
      
      if (cart.length > maxCartSize) {
        console.warn(`Cart size (${cart.length}) exceeds maximum (${maxCartSize}), truncating...`);
        cart = cart.slice(-maxCartSize); // Keep only the most recent items
      }
      
      // Check individual item sizes
      const sanitizedCart = cart.map(item => {
        const itemStr = JSON.stringify(item);
        if (itemStr.length > maxItemSize) {
          const truncatedItem = {
            id: item.id,
            type: item.type,
            name: item.name || 'Custom Cable',
            price: item.price,
            addedAt: item.addedAt,
            // Preserve essential configuration data for pricing accuracy
            connectors: item.connectors,
            hybridConnectors: item.hybridConnectors,
            wireGauge: item.wireGauge,
            finishing: item.finishing,
            length: item.length,
            style: item.style,
            sku: item.sku,
            _truncated: true
          };
          return truncatedItem;
        }
        return item;
      });
      
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(sanitizedCart));
      localStorage.setItem(CART_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
      
      // If localStorage is full, try to clear old data
      if (error.name === 'QuotaExceededError') {
        this.clearCart();
      }
    }
  }

  // Get current cart
  getCart() {
    return this.cart;
  }

  // Add item to cart
  addToCart(item) {
    
    const newCart = [...this.cart, {
      ...item,
      id: item.id || Date.now(),
      addedAt: Date.now()
    }];
    this.cart = newCart;
    this.saveCart(newCart);
    this._hasFreshOperations = true; // Mark that there have been fresh operations
    this.notifyListeners(newCart);
    return newCart;
  }

  // Remove item from cart by index
  removeFromCart(index) {
    const newCart = this.cart.filter((_, i) => i !== index);
    this.cart = newCart;
    this.saveCart(newCart);
    this._hasFreshOperations = true; // Mark that there have been fresh operations
    this.notifyListeners(newCart);
    return newCart;
  }

  // Remove item from cart by ID
  removeFromCartById(id) {
    const newCart = this.cart.filter(item => item.id !== id);
    this.cart = newCart;
    this.saveCart(newCart);
    this._hasFreshOperations = true; // Mark that there have been fresh operations
    this.notifyListeners(newCart);
    return newCart;
  }

  // Update cart item
  updateCartItem(id, updates) {
    const newCart = this.cart.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
    this.cart = newCart;
    this.saveCart(newCart);
    this._hasFreshOperations = true; // Mark that there have been fresh operations
    this.notifyListeners(newCart);
    return newCart;
  }

  // Clear entire cart
  clearCart() {
    this.cart = [];
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_TIMESTAMP_KEY);
    this._hasFreshOperations = true; // Mark that there have been fresh operations
    this.notifyListeners([]);
    return [];
  }

  // Get cart count
  getCartCount() {
    return this.cart.length;
  }

  // Check if cart is empty
  isEmpty() {
    return this.cart.length === 0;
  }

  // Subscribe to cart changes
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notify all listeners of cart changes
  notifyListeners(cart) {
    this.listeners.forEach(listener => {
      try {
        listener(cart);
      } catch (error) {
        console.error('Error in cart listener:', error);
      }
    });
  }

  // Export cart data (for backup/transfer)
  exportCart() {
    return {
      cart: this.cart,
      timestamp: Date.now(),
      version: '1.0'
    };
  }

  // Import cart data (for restore)
  importCart(data) {
    try {
      if (data && data.cart && Array.isArray(data.cart)) {
        this.cart = data.cart;
        this.saveCart(this.cart);
        this.notifyListeners(this.cart);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importing cart:', error);
      return false;
    }
  }

  // Get cart statistics
  getCartStats() {
    const totalItems = this.cart.length;
    const totalValue = this.cart.reduce((sum, item) => sum + (item.price || 0), 0);
    const oldestItem = this.cart.length > 0 ? Math.min(...this.cart.map(item => item.addedAt)) : null;
    const newestItem = this.cart.length > 0 ? Math.max(...this.cart.map(item => item.addedAt)) : null;

    return {
      totalItems,
      totalValue,
      oldestItem: oldestItem ? new Date(oldestItem) : null,
      newestItem: newestItem ? new Date(newestItem) : null,
      isEmpty: this.isEmpty()
    };
  }

  // Handle mobile-specific issues
  handleMobileIssues() {
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Mobile device detected, applying mobile-specific optimizations
      
      // Reduce cart size limits for mobile, but keep it reasonable
      const maxCartSize = 35; // Increased from 25 to 35 for better user experience
      
      if (this.cart.length > maxCartSize) {
        console.warn(`Mobile: Cart size (${this.cart.length}) exceeds mobile maximum (${maxCartSize}), truncating...`);
        this.cart = this.cart.slice(-maxCartSize);
        this.saveCart(this.cart);
      }
    }
  }

  // Test localStorage availability
  testLocalStorage() {
    try {
      const testKey = 'nlfx_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.error('localStorage not available:', error);
      return false;
    }
  }
}

// Create singleton instance
const cartService = new CartService();

export default cartService;